import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
const termsContent = `TÉRMINOS Y CONDICIONES

Última actualización: Abril 2026

1. ACEPTACIÓN DE LOS TÉRMINOS
Al acceder y realizar una compra en este sitio web, aceptás cumplir y estar sujeto/a a los siguientes términos y condiciones de uso. Si no estás de acuerdo con alguna parte de estos términos, no debés utilizar este sitio.

2. PRODUCTO DIGITAL
El producto ofrecido es un protocolo digital descargable ("El Escudo de Aburrimiento"). Se trata de material educativo e informativo. No constituye asesoramiento psicológico, médico ni legal profesional.

3. ENTREGA
Una vez confirmado el pago, recibirás acceso inmediato al material digital a través del correo electrónico proporcionado durante la compra. Es tu responsabilidad asegurarte de que la dirección de correo electrónico sea correcta.

4. POLÍTICA DE REEMBOLSO
Ofrecemos una garantía de satisfacción de 7 (siete) días corridos desde la fecha de compra. Si el producto no cumple con tus expectativas, podés solicitar un reembolso completo dentro de ese plazo enviando un correo electrónico.

5. PROPIEDAD INTELECTUAL
Todo el contenido del producto, incluyendo textos, gráficos, audios y diseño, está protegido por derechos de autor. Queda prohibida su reproducción, distribución o reventa total o parcial sin autorización expresa por escrito.

6. LIMITACIÓN DE RESPONSABILIDAD
El material proporcionado tiene fines educativos. Los resultados pueden variar según cada situación particular. No garantizamos resultados específicos. En casos de violencia física grave o situaciones de riesgo, se recomienda acudir a las autoridades competentes.

7. MODIFICACIONES
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigencia a partir de su publicación en este sitio.

8. LEY APLICABLE
Estos términos se rigen por las leyes de la República Argentina.`;
const privacyContent = `POLÍTICAS DE PRIVACIDAD

Última actualización: Abril 2026

1. INFORMACIÓN QUE RECOPILAMOS
Recopilamos únicamente la información necesaria para procesar tu compra:
• Nombre completo
• Dirección de correo electrónico
• Información de pago (procesada de forma segura por Mercado Pago)

2. USO DE LA INFORMACIÓN
La información recopilada se utiliza exclusivamente para:
• Procesar y entregar tu compra
• Enviar el producto digital adquirido
• Comunicarnos en caso de ser necesario respecto a tu pedido

3. PROTECCIÓN DE DATOS
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.

4. PROCESAMIENTO DE PAGOS
Los pagos son procesados de forma segura a través de Mercado Pago. No almacenamos datos de tarjetas de crédito ni información financiera sensible en nuestros servidores.

5. COOKIES
Este sitio puede utilizar cookies técnicas necesarias para su funcionamiento. No utilizamos cookies de rastreo publicitario de terceros.

6. COMPARTIR INFORMACIÓN
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción es el procesador de pagos (Mercado Pago) necesario para completar la transacción.

7. TUS DERECHOS
De acuerdo con la Ley 25.326 de Protección de Datos Personales de la República Argentina, tenés derecho a:
• Acceder a tus datos personales
• Solicitar la rectificación de datos inexactos
• Solicitar la supresión de tus datos

8. MENORES DE EDAD
Este sitio y producto están dirigidos a madres, padres y tutores adultos. No recopilamos intencionalmente información de menores de 18 años.

9. CAMBIOS EN ESTA POLÍTICA
Nos reservamos el derecho de actualizar esta política de privacidad. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.

10. LEY APLICABLE
Esta política se rige por la Ley 25.326 de Protección de Datos Personales y demás normativa aplicable de la República Argentina.`;
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: LegalModalProps) {
  return <AnimatePresence data-id="element-38">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-dark-coffee/60 backdrop-blur-sm" onClick={onClose} data-id="element-39">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="bg-creme rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-40">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-beige" data-id="element-41">
              <h3 className="font-playfair text-lg font-semibold text-dark-coffee" data-id="element-42">
                {title}
              </h3>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-beige transition-colors text-soft-brown" data-id="element-43">
                <XIcon className="w-5 h-5" data-id="element-44" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)]" data-id="element-45">
              <pre className="whitespace-pre-wrap font-sans text-sm text-soft-brown leading-relaxed" data-id="element-46">
                {content}
              </pre>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'terms' | 'privacy' | null>(null);
  return <>
      <footer className="w-full text-soft-brown py-12 px-6 text-sm bg-creme pt-[15px] pb-[15px] border-t border-beige" data-id="element-47">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 bg-creme" data-id="element-48">
          <div className="flex items-center gap-6" data-id="element-49">
            <button onClick={() => setActiveModal('terms')} className="text-soft-brown hover:text-dark-coffee transition-colors" data-id="element-50">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="text-soft-brown hover:text-dark-coffee transition-colors" data-id="element-51">
              Políticas de Privacidad
            </button>
          </div>

          <div className="text-center md:text-right text-xs opacity-80 text-soft-brown" data-id="element-52">
            &copy; {new Date().getFullYear()} Todos los derechos reservados.
            <br data-id="element-53" />
            Este sitio no forma parte de Facebook o Meta Inc.
          </div>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" content={termsContent} data-id="element-54" />
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Políticas de Privacidad" content={privacyContent} data-id="element-55" />
    </>;
}